import dayjs from "dayjs"
import pluralize from "pluralize"
import { useMemo } from "react"
import * as Yup from "yup"

import useIsDesktop from "@hooks/useIsDesktop"
import useSlugExtractor from "@hooks/useSlugExtractor"

import { dayjsDayToWeekdayIndex, getMonthlyRecurrenceOptions } from "@utils/formatRecurrence"

import { Button, Chip, Label } from "@atoms"

import { Dialog, RadioCard, RadioGroup, Select } from "@molecules"
import { DateInput } from "@molecules/Form/Form"
import GenericForm from "@molecules/Form/GenericForm/GenericForm"
import { MutationErrorBase } from "@molecules/Form/GenericForm/GenericForm.types"
import InputGroup from "@molecules/Form/inputs/InputGroup/InputGroup"
import NumberInput from "@molecules/Form/inputs/NumberInput/NumberInput"
import toast from "@molecules/Toast/Toast"

import { JOB_SERIES_ROUTES } from "@routes/jobs-series"

import { FREQUENCY_OPTIONS, RecurrenceFrequency, WEEKDAY_OPTIONS } from "@constants/choices"

import styles from "./JobSeriesEditDialog.module.scss"
import { JobSeriesEditDialogProps, JobSeriesEditFormFields, JobSeriesEditPayload } from "./JobSeriesEditDialog.types"

const formSchema: Yup.ObjectSchema<JobSeriesEditFormFields> = Yup.object().shape({
    recurrence_end: Yup.date().nullable().defined(),
    recurrence_interval: Yup.number().min(1, "Must be at least 1").required("Required"),
    recurrence_frequency: Yup.number()
        .oneOf([
            RecurrenceFrequency.DAILY,
            RecurrenceFrequency.WEEKLY,
            RecurrenceFrequency.MONTHLY,
            RecurrenceFrequency.YEARLY,
        ])
        .required("Required"),
    weekly_recurrence: Yup.array().of(Yup.number().required()).required(),
    monthly_recurrence: Yup.number().required(),
})

export default function JobSeriesEditDialog(props: JobSeriesEditDialogProps) {
    const { jobSeries, isOpen, onOpenChange, onSuccess } = props

    const [endpoint] = useSlugExtractor([JOB_SERIES_ROUTES.DETAILS_API], jobSeries.id)

    const monthlyRecurrenceOptions = useMemo(
        () => getMonthlyRecurrenceOptions(jobSeries.recurrence_start),
        [jobSeries.recurrence_start],
    )

    const isDesktop = useIsDesktop()

    const handleSuccess = () => {
        toast({
            variant: "default",
            title: "Job series updated",
            type: "success",
        })
        onSuccess()
        onOpenChange(false)
    }

    const defaultValues: JobSeriesEditFormFields = {
        recurrence_end: jobSeries.recurrence_end ? new Date(jobSeries.recurrence_end) : null,
        recurrence_interval: jobSeries.recurrence_interval,
        recurrence_frequency: jobSeries.recurrence_frequency,
        weekly_recurrence: jobSeries.weekly_recurrence ?? [],
        monthly_recurrence: jobSeries.monthly_recurrence ?? 0,
    }

    const payloadFormatter = (data: JobSeriesEditFormFields): JobSeriesEditPayload => {
        const frequency = Number(data.recurrence_frequency)
        const isWeekly = frequency === RecurrenceFrequency.WEEKLY
        const isMonthly = frequency === RecurrenceFrequency.MONTHLY

        return {
            recurrence_end: data.recurrence_end ? data.recurrence_end.toISOString().split("T")[0] : null,
            recurrence_interval: data.recurrence_interval,
            recurrence_frequency: frequency,
            weekly_recurrence: isWeekly ? data.weekly_recurrence : null,
            monthly_recurrence: isMonthly ? data.monthly_recurrence : null,
        }
    }

    return (
        <Dialog.Root isOpen={isOpen} onOpenChange={onOpenChange} mode="dialog" variant="default">
            <Dialog.Content className={styles.dialogContent}>
                <GenericForm<JobSeriesEditFormFields, JobSeries, MutationErrorBase, JobSeriesEditPayload>
                    formConfig={{ defaultValues }}
                    formValidationSchema={formSchema}
                    mutationConfig={{
                        genericErrorMessage: "Could not update job series",
                        method: "PATCH",
                        endpoint,
                        payloadFormatter,
                    }}
                    onMutationSuccess={handleSuccess}
                >
                    {({ isPending, form }) => {
                        const frequency = form.watch("recurrence_frequency")
                        const interval = form.watch("recurrence_interval")
                        const monthlyRecurrence = form.watch("monthly_recurrence")
                        const weeklyRecurrence = form.watch("weekly_recurrence")

                        // frequency can be a number (initial) or string (after Select change)
                        const isWeekly = Number(frequency) === RecurrenceFrequency.WEEKLY
                        const isMonthly = Number(frequency) === RecurrenceFrequency.MONTHLY

                        const toggleWeekday = (day: number) => {
                            const current = form.getValues("weekly_recurrence")
                            const isSelected = current.includes(day)

                            // Min 1: Don't allow deselecting if only 1 chip is selected
                            if (isSelected && current.length <= 1) {
                                return
                            }

                            const newValue = isSelected
                                ? current.filter((weekday) => weekday !== day)
                                : [...current, day].sort((left, right) => left - right)
                            form.setValue("weekly_recurrence", newValue, { shouldDirty: true })
                        }

                        const handleFrequencyChange = (value: string) => {
                            const newFrequency = Number(value)
                            if (newFrequency === RecurrenceFrequency.WEEKLY) {
                                const currentWeekly = form.getValues("weekly_recurrence")
                                if (currentWeekly.length === 0) {
                                    const startDayIndex = dayjsDayToWeekdayIndex(
                                        dayjs(jobSeries.recurrence_start).day(),
                                    )
                                    form.setValue("weekly_recurrence", [startDayIndex], { shouldDirty: true })
                                }
                            }
                        }

                        return (
                            <>
                                <Dialog.Header.Root>
                                    <Dialog.Header.Title title="Details" />
                                    <Dialog.Header.CloseButton />
                                </Dialog.Header.Root>
                                <Dialog.Body className={styles.dialogBody}>
                                    <div className={styles.row}>
                                        <DateInput
                                            name="recurrence_end"
                                            label={{ label: "Series end date", isOptional: true }}
                                            size="md"
                                            withDatePicker={true}
                                        />
                                        <Select.Root
                                            defaultSelectedItem={{
                                                value: String(jobSeries.recurrence_frequency),
                                                label:
                                                    FREQUENCY_OPTIONS.find(
                                                        (opt) => opt.value === jobSeries.recurrence_frequency,
                                                    )?.label ?? "",
                                            }}
                                        >
                                            <InputGroup label="Repeat every">
                                                <NumberInput
                                                    name="recurrence_interval"
                                                    size="md"
                                                    min={1}
                                                    isFullWidth={true}
                                                />
                                                <Select.Input
                                                    name="recurrence_frequency"
                                                    size="md"
                                                    isControlled={true}
                                                    placeholder="Frequency"
                                                    className={styles.frequencyInput}
                                                    options={FREQUENCY_OPTIONS.map((opt) => ({
                                                        value: String(opt.value),
                                                        label: pluralize(opt.label, interval),
                                                    }))}
                                                />
                                            </InputGroup>
                                            <Select.Viewport>
                                                <Select.List title="Frequency">
                                                    <Select.ListGroup>
                                                        {FREQUENCY_OPTIONS.map((opt) => (
                                                            <Select.ListItem
                                                                key={opt.value}
                                                                variant="icon"
                                                                item={{
                                                                    value: String(opt.value),
                                                                    label: pluralize(opt.label, interval),
                                                                }}
                                                                onSelect={() =>
                                                                    handleFrequencyChange(String(opt.value))
                                                                }
                                                            />
                                                        ))}
                                                    </Select.ListGroup>
                                                </Select.List>
                                            </Select.Viewport>
                                        </Select.Root>
                                    </div>

                                    {isWeekly && (
                                        <div className={styles.repeatOnContainer}>
                                            <Label
                                                label="Repeat on"
                                                hint={{
                                                    body: "Select the weekdays when jobs will be created (e.g. every Tuesday and Thursday).",
                                                    colorScheme: "blue",
                                                }}
                                            />
                                            <div className={styles.weekdayChips}>
                                                {WEEKDAY_OPTIONS.map((day) => (
                                                    <Chip
                                                        key={day.value}
                                                        isSelected={weeklyRecurrence.includes(day.value)}
                                                        onClick={() => toggleWeekday(day.value)}
                                                        className={styles.weekdayChip}
                                                    >
                                                        {!isDesktop ? day.short : day.label}
                                                    </Chip>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {isMonthly && (
                                        <div className={styles.repeatOnContainer}>
                                            <Label
                                                label="Repeat on"
                                                hint={{
                                                    body: "Choose how jobs repeat each month: on a specific day (e.g. 23rd) or on a weekday pattern (e.g. 4th Saturday).",
                                                    colorScheme: "blue",
                                                }}
                                            />
                                            <RadioGroup
                                                isHorizontal={true}
                                                value={String(monthlyRecurrence)}
                                                onValueChange={(value) => {
                                                    form.setValue("monthly_recurrence", Number(value), {
                                                        shouldDirty: true,
                                                    })
                                                }}
                                            >
                                                {monthlyRecurrenceOptions.map((option) => (
                                                    <RadioCard
                                                        key={option.value}
                                                        value={String(option.value)}
                                                        label={option.label}
                                                        showLabel={true}
                                                    />
                                                ))}
                                            </RadioGroup>
                                        </div>
                                    )}
                                </Dialog.Body>
                                <Dialog.Footer.Root>
                                    <Button
                                        variant="ghost"
                                        size="lg"
                                        type="button"
                                        onClick={() => onOpenChange(false)}
                                    >
                                        Cancel
                                    </Button>
                                    <Button variant="solid" size="lg" type="submit" isProcessing={isPending}>
                                        Save
                                    </Button>
                                </Dialog.Footer.Root>
                            </>
                        )
                    }}
                </GenericForm>
            </Dialog.Content>
        </Dialog.Root>
    )
}
